import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { Sport } from "../types/activity";
import {
  safeGetJSON,
  safeRemoveItem,
  safeSetJSON,
} from "../utils/localStorageUtils";
import {
  CompetitionLevel,
  DEFAULT_DOB,
  OnboardingSex,
} from "../components/onboarding/onboardingCopy";
import { usernameSyntaxError } from "../components/onboarding/onboardingValidation";

/**
 * Step enum mirrors iOS `OnboardingStep` (Onboarding/OnboardingState.swift),
 * plus a PWA-only `plan` step (Free / Plus / Premium) after notifications.
 */
export const ONBOARDING_STEPS = [
  "welcome",
  "createAccount",
  "email",
  "inviteCode",
  "competitionLevel",
  "sports",
  "sexAndDob",
  "success",
  "difference",
  "howPeakHelps",
  "day1",
  "notifications",
  "plan",
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export type UsernameStatus =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "available" }
  | { kind: "taken" }
  | { kind: "error"; message: string };

export interface OnboardingWellnessDraft {
  sleepTime: number;
  mood: number;
  recovery: number;
  sleep: number;
  stress: number;
  health: number;
  physical: number;
  soreness: number;
  sick: boolean;
  injured: boolean;
  trainingCapacity: "full" | "modified" | "none";
}

export const DEFAULT_WELLNESS: OnboardingWellnessDraft = {
  sleepTime: 3,
  mood: 3,
  recovery: 3,
  sleep: 3,
  stress: 3,
  health: 3,
  physical: 3,
  soreness: 0,
  sick: false,
  injured: false,
  trainingCapacity: "full",
};

/** The part of the state that survives a refresh (never the password). */
export interface OnboardingDraft {
  step: OnboardingStep;
  email: string;
  inviteCode: string;
  competitionLevel: CompetitionLevel | null;
  selectedSportIDs: string[];
  sex: OnboardingSex | null;
  dob: string;
  firstName: string;
  username: string;
  successLook: string;
  /** A Firebase Auth user exists (email step or social provider). */
  accountReady: boolean;
  /** Apple / Google vouched for the email. Password signups verify later in Settings. */
  emailVerifiedByProvider: boolean;
  /** Required before an account can be created (Terms and Privacy Policy). */
  legalAccepted: boolean;
  /** The first wellness log has been written for today. */
  wellnessLogged: boolean;
  /** Resuming for an Auth user who already has a users/{uid} profile with onboardingComplete false. */
  existingProfile: boolean;
  /** Local hour picked on the reminder step; null = skipped. Written at finish. */
  reminderHour: number | null;
  wellness: OnboardingWellnessDraft;
}

export interface OnboardingUiState extends OnboardingDraft {
  password: string;
  usernameStatus: UsernameStatus;
  authBusy: boolean;
  authError: string | null;
  submitting: boolean;
  submitStatus: string | null;
  submitError: string | null;
  wellnessSubmitting: boolean;
  wellnessSubmitError: string | null;
  availableSports: Sport[];
  sportsLoading: boolean;
  sportsLoadError: string | null;
  finished: boolean;
}

export const INITIAL_DRAFT: OnboardingDraft = {
  step: "welcome",
  email: "",
  inviteCode: "",
  competitionLevel: null,
  selectedSportIDs: [],
  sex: null,
  dob: DEFAULT_DOB,
  firstName: "",
  username: "",
  successLook: "",
  accountReady: false,
  emailVerifiedByProvider: false,
  legalAccepted: false,
  wellnessLogged: false,
  existingProfile: false,
  reminderHour: null,
  wellness: DEFAULT_WELLNESS,
};

export const INITIAL_STATE: OnboardingUiState = {
  ...INITIAL_DRAFT,
  password: "",
  usernameStatus: { kind: "idle" },
  authBusy: false,
  authError: null,
  submitting: false,
  submitStatus: null,
  submitError: null,
  wellnessSubmitting: false,
  wellnessSubmitError: null,
  availableSports: [],
  sportsLoading: false,
  sportsLoadError: null,
  finished: false,
};

export type OnboardingAction =
  | { type: "continue" }
  | { type: "back" }
  | { type: "goTo"; step: OnboardingStep }
  | { type: "openEmail" }
  | { type: "skipInviteCode" }
  | { type: "skipGoal" }
  | { type: "setField"; field: "email" | "password" | "inviteCode" | "firstName" | "successLook" | "dob"; value: string }
  | { type: "setLegalAccepted"; accepted: boolean }
  | { type: "setUsername"; value: string }
  | { type: "setUsernameStatus"; status: UsernameStatus }
  | { type: "selectCompetitionLevel"; value: CompetitionLevel }
  | { type: "selectSex"; value: OnboardingSex }
  | { type: "toggleSport"; id: string }
  | { type: "sportsLoading" }
  | { type: "sportsLoaded"; sports: Sport[] }
  | { type: "sportsFailed"; message: string }
  | { type: "authStart" }
  | { type: "authError"; message: string | null }
  | { type: "accountCreated"; email: string; firstName?: string; emailVerifiedByProvider: boolean; next: OnboardingStep }
  | { type: "setReminderHour"; hour: number | null }
  | { type: "updateWellness"; patch: Partial<OnboardingWellnessDraft> }
  | { type: "wellnessSubmitStart" }
  | { type: "wellnessSubmitDone" }
  | { type: "wellnessSubmitError"; message: string }
  | { type: "submitStart"; status: string }
  | { type: "submitStatus"; status: string }
  | { type: "submitError"; message: string }
  | { type: "submitDone" }
  | { type: "restore"; draft: Partial<OnboardingDraft> }
  | { type: "reset" };

/** Progress capsule index (1–4) shown on the four intake steps, else null. */
export function intakeProgress(step: OnboardingStep): number | null {
  switch (step) {
    case "competitionLevel": return 1;
    case "sports": return 2;
    case "sexAndDob": return 3;
    case "success": return 4;
    default: return null;
  }
}

/** iOS `continueTapped` plus the PWA `plan` step. */
export function nextStep(step: OnboardingStep): OnboardingStep {
  switch (step) {
    case "welcome": return "createAccount";
    case "createAccount": return "competitionLevel";
    case "email": return "inviteCode";
    case "inviteCode": return "competitionLevel";
    case "competitionLevel": return "sports";
    case "sports": return "sexAndDob";
    case "sexAndDob": return "success";
    case "success": return "difference";
    case "difference": return "howPeakHelps";
    case "howPeakHelps": return "day1";
    case "day1": return "notifications";
    case "notifications": return "plan";
    case "plan": return "plan";
  }
}

/**
 * iOS `goBack`. One PWA difference: once the Auth account exists, backing out
 * of competition level lands on the invite step (the account can't be
 * re-created, and an existing-profile resume has no create-account step).
 */
export function previousStep(state: Pick<OnboardingUiState, "step" | "accountReady" | "existingProfile">): OnboardingStep {
  switch (state.step) {
    case "welcome": return "welcome";
    case "createAccount": return "welcome";
    case "email": return "createAccount";
    case "inviteCode": return "createAccount";
    case "competitionLevel":
      if (state.existingProfile) return "competitionLevel";
      return state.accountReady ? "inviteCode" : "createAccount";
    case "sports": return "competitionLevel";
    case "sexAndDob": return "sports";
    case "success": return "sexAndDob";
    case "difference": return "success";
    case "howPeakHelps": return "difference";
    case "day1": return "howPeakHelps";
    case "notifications": return "day1";
    case "plan": return "notifications";
  }
}

/** iOS `canContinue(from:)`. */
export function canContinue(state: OnboardingUiState, step: OnboardingStep = state.step): boolean {
  switch (step) {
    case "sports": return state.selectedSportIDs.length > 0;
    case "competitionLevel": return state.competitionLevel !== null;
    case "sexAndDob":
      return (
        state.sex !== null &&
        state.dob.length > 0 &&
        state.firstName.trim().length > 0 &&
        state.usernameStatus.kind === "available"
      );
    case "success": return state.successLook.trim().length > 0;
    case "email": return state.email.length > 0 && state.password.length > 0 && !state.authBusy;
    case "inviteCode": return state.inviteCode.trim().length > 0;
    default: return true;
  }
}

export function onboardingReducer(state: OnboardingUiState, action: OnboardingAction): OnboardingUiState {
  switch (action.type) {
    case "continue":
      if (state.step === "plan") return { ...state, finished: true };
      return { ...state, step: nextStep(state.step) };
    case "back":
      return { ...state, step: previousStep(state), submitError: null, authError: null };
    case "goTo":
      return { ...state, step: action.step };
    case "openEmail":
      return { ...state, step: "email", authError: null };
    case "skipInviteCode":
      return { ...state, inviteCode: "", step: "competitionLevel" };
    case "skipGoal":
      return { ...state, successLook: "", step: "difference" };
    case "setField":
      return {
        ...state,
        [action.field]: action.value,
        ...(action.field === "email" || action.field === "password" ? { authError: null } : {}),
      };
    case "setLegalAccepted":
      return { ...state, legalAccepted: action.accepted };
    case "setUsername":
      return { ...state, username: action.value, usernameStatus: { kind: "idle" } };
    case "setUsernameStatus":
      return { ...state, usernameStatus: action.status };
    case "selectCompetitionLevel":
      return { ...state, competitionLevel: action.value };
    case "selectSex":
      return { ...state, sex: action.value };
    case "toggleSport": {
      const has = state.selectedSportIDs.includes(action.id);
      return {
        ...state,
        selectedSportIDs: has
          ? state.selectedSportIDs.filter((id) => id !== action.id)
          : [...state.selectedSportIDs, action.id],
      };
    }
    case "sportsLoading":
      return { ...state, sportsLoading: true, sportsLoadError: null };
    case "sportsLoaded":
      return {
        ...state,
        availableSports: action.sports,
        sportsLoading: false,
        sportsLoadError: action.sports.length === 0 ? "No sports available right now. Try again later." : null,
      };
    case "sportsFailed":
      return { ...state, sportsLoading: false, sportsLoadError: action.message };
    case "authStart":
      return { ...state, authBusy: true, authError: null };
    case "authError":
      return { ...state, authBusy: false, authError: action.message };
    case "accountCreated":
      return {
        ...state,
        authBusy: false,
        authError: null,
        accountReady: true,
        emailVerifiedByProvider: action.emailVerifiedByProvider,
        email: state.email || action.email,
        firstName: state.firstName || action.firstName || "",
        password: "",
        step: action.next,
      };
    case "setReminderHour":
      return { ...state, reminderHour: action.hour, step: "plan" };
    case "updateWellness":
      return { ...state, wellness: { ...state.wellness, ...action.patch } };
    case "wellnessSubmitStart":
      return { ...state, wellnessSubmitting: true, wellnessSubmitError: null };
    case "wellnessSubmitDone":
      return { ...state, wellnessSubmitting: false, wellnessLogged: true, step: "day1" };
    case "wellnessSubmitError":
      return { ...state, wellnessSubmitting: false, wellnessSubmitError: action.message };
    case "submitStart":
      return { ...state, submitting: true, submitStatus: action.status, submitError: null };
    case "submitStatus":
      return { ...state, submitStatus: action.status };
    case "submitError":
      return { ...state, submitting: false, submitStatus: null, submitError: action.message };
    case "submitDone":
      return { ...state, submitting: false, submitStatus: null, finished: true };
    case "restore":
      return { ...state, ...action.draft, wellness: { ...DEFAULT_WELLNESS, ...(action.draft.wellness ?? {}) } };
    case "reset":
      return { ...INITIAL_STATE, availableSports: state.availableSports };
    default:
      return state;
  }
}

export const ONBOARDING_DRAFT_KEY = "onboardingDraft";

const DRAFT_KEYS: Array<keyof OnboardingDraft> = [
  "step",
  "email",
  "inviteCode",
  "competitionLevel",
  "selectedSportIDs",
  "sex",
  "dob",
  "firstName",
  "username",
  "successLook",
  "accountReady",
  "emailVerifiedByProvider",
  "legalAccepted",
  "wellnessLogged",
  "existingProfile",
  "reminderHour",
  "wellness",
];

export function pickDraft(state: OnboardingUiState): OnboardingDraft {
  const draft = {} as OnboardingDraft;
  for (const key of DRAFT_KEYS) (draft as any)[key] = state[key];
  return draft;
}

export function loadDraft(): Partial<OnboardingDraft> | null {
  const raw = safeGetJSON<Partial<OnboardingDraft> | null>(ONBOARDING_DRAFT_KEY, null);
  if (!raw || typeof raw !== "object") return null;
  if (raw.step && !ONBOARDING_STEPS.includes(raw.step)) return null;
  return raw;
}

export function saveDraft(state: OnboardingUiState): void {
  safeSetJSON(ONBOARDING_DRAFT_KEY, pickDraft(state));
}

export function clearDraft(): void {
  safeRemoveItem(ONBOARDING_DRAFT_KEY);
}

export interface UseOnboardingWizardOptions {
  /** Called 400 ms after the username settles; resolves true when free. */
  checkUsername?: (username: string) => Promise<boolean>;
  /** Persist the draft to localStorage (default true). */
  persist?: boolean;
  /** Skip restoring a saved draft on mount. */
  ignoreSavedDraft?: boolean;
}

const USERNAME_DEBOUNCE_MS = 400;

/**
 * State + pure step logic for the onboarding wizard. Side effects that talk
 * to Firebase (account creation, wellness write, final profile write) live in
 * `OnboardingWizard` / `onboardingService`; this hook owns the state machine,
 * the draft persistence and the debounced username availability check.
 */
export function useOnboardingWizard(options: UseOnboardingWizardOptions = {}) {
  const { checkUsername, persist = true, ignoreSavedDraft = false } = options;

  const [state, dispatch] = useReducer(onboardingReducer, INITIAL_STATE, (initial) => {
    if (ignoreSavedDraft) return initial;
    const draft = loadDraft();
    return draft ? onboardingReducer(initial, { type: "restore", draft }) : initial;
  });

  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!persist) return;
    if (state.finished) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => saveDraft(state), 120);
    return () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [state, persist]);

  const usernameTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestUsername = useRef(state.username);
  latestUsername.current = state.username;

  const setUsername = useCallback(
    (value: string) => {
      dispatch({ type: "setUsername", value });
      if (usernameTimer.current) clearTimeout(usernameTimer.current);
      if (!checkUsername) return;
      if (usernameSyntaxError(value) !== null) return;
      usernameTimer.current = setTimeout(async () => {
        if (latestUsername.current !== value) return;
        dispatch({ type: "setUsernameStatus", status: { kind: "checking" } });
        try {
          const available = await checkUsername(value);
          if (latestUsername.current !== value) return;
          dispatch({ type: "setUsernameStatus", status: { kind: available ? "available" : "taken" } });
        } catch (error) {
          if (latestUsername.current !== value) return;
          dispatch({
            type: "setUsernameStatus",
            status: { kind: "error", message: error instanceof Error ? error.message : "Could not check that username." },
          });
        }
      }, USERNAME_DEBOUNCE_MS);
    },
    [checkUsername],
  );

  useEffect(() => () => {
    if (usernameTimer.current) clearTimeout(usernameTimer.current);
  }, []);

  const progress = useMemo(() => intakeProgress(state.step), [state.step]);

  return {
    state,
    dispatch,
    progress,
    canContinue: (step?: OnboardingStep) => canContinue(state, step),
    setUsername,
    continueTapped: () => dispatch({ type: "continue" }),
    goBack: () => dispatch({ type: "back" }),
    clearDraft,
  };
}
